<template>
  <div>
    <div class="grid-x">
      <div class="cell auto">
        <h2 class="text-bold">Add {{ recordType }}</h2>
      </div>
      <div class="cell shrink">
        <a class="no-color padding-right-small" @click="$emit('expand')"><span class="fas fa-expand"></span></a>
      </div>
    </div>

    <form-form @submit="handleSubmit" :validator="$v" :submit-error="submitError">

        <fieldset>
          <form-radio-element label="Pledge" value="Pledge" v-model="recordType"></form-radio-element>
          <form-radio-element label="Contribution" value="Contribution" v-model="recordType"></form-radio-element>
          <form-radio-element label="Ask" value="Ask" v-model="recordType"></form-radio-element>
        </fieldset>

      <form-datepicker
        placeholder="Date"
        v-model="date"
        :pick-time="true"
        :right="true"
        :validator="$v.date"
        @input="dateChanged" />

      <template v-if="isPledge || isContribution">
        <form-input type="number" v-model="amount" placeholder="Amount" :validator="$v.amount" :step="0.01" />
      </template>

      <!--Ask Result-->
      <template v-if="isAsk">
        <form-group>
          <ask-result-buttons v-model="askResult" />
        </form-group>
        <form-input type="number" v-model="amount" placeholder="Pledge Amount (optional)" :validator="$v.amount" :step="0.01" v-if="askResult === 'Yes'" />
      </template>

      <add-note-field placeholder="Add note..." :start-lines="2" v-model="note" />
      <form-submit :validator="$v">
        Add {{ recordType }}
      </form-submit>
    </form-form>

    <ul class="list-padded" v-if="recordHistory">
      <li v-for="{ type, record } in recordHistory" :key="`${type}${record.id}`">
        <!--Contribution-->
        <template v-if="type === 'contribution'">
          <div>
            <strong>Contribution: {{ record.amount | currency }}</strong>
            <div v-if="record.note">
              {{ record.note.note }}
            </div>
          </div>
          <div class="text-muted">
            <small>
              <em>{{ record.date | dateTimeString }} | <a class="text-danger" @click="deleteContribution(record.id)">Delete</a></em>
            </small>
          </div>
        </template>

        <!--Pledge-->
        <template v-if="type === 'pledge'">
          <div :class="{ 'text-strike': record.is_dismissed }">
            <strong>Pledge<span v-if="record.amount !== null">: {{ record.amount | currency }}</span></strong>
            <div v-if="record.note">
              {{ record.note.note }}
            </div>
          </div>
          <div class="text-muted">
            <small>
              <em>
                {{ record.date | dateTimeString }} |
                <template v-if="record.is_dismissed">
                  <a @click="dismissContributionPledge(record.id, true)">Un-dismiss</a> |
                  <a class="text-danger" @click="deleteContributionPledge(record.id)">Delete</a>
                </template>
                <template v-else>
                  <a @click="dismissContributionPledge(record.id)">Dismiss</a>
                </template>
              </em>
            </small>
          </div>
        </template>

        <!--Ask-->
        <template v-if="type === 'ask'">
          <div>
            <strong>Asked<span v-if="record.amount !== null">: {{ record.amount | currency }}</span><span v-if="record.result"> - {{ record.result }}</span>
            </strong>
            <div v-if="record.note">
              {{ record.note.note }}
            </div>
          </div>
          <div class="text-muted">
            <small>
              <em>{{ record.date | dateTimeString }} | <a class="text-danger" @click="deleteContributionAsk(record.id)">Delete</a></em>
            </small>
          </div>
        </template>

      </li>
    </ul>
  </div>
</template>

<script>
import { formsMixin } from '../../../mixins/forms/formsMixin'
import { required, requiredIf } from 'vuelidate/lib/validators'
import moment from 'moment'
import {
  addContributionMutation,
  deleteContributionMutation,
  contactContributionsQuery,
  addContributionAskMutation, contactContributionAsksQuery, deleteContributionAskMutation
} from '../../../graphql/contributions'
import {
  addContributionPledgeMutation,
  contactContributionPledgesQuery,
  deleteContributionPledgeMutation, dismissContributionPledgeMutation
} from '../../../graphql/contributionPledges'
import { contactQuery } from '../../../graphql/contacts'
import AskResultButtons from '../../../components/LogCall/AskResultButtons'
import AddNoteField from '../../../components/Contacts/AddNoteField'

export default {
  components: {
    AddNoteField,
    AskResultButtons
  },
  mixins: [
    formsMixin
  ],
  props: {
    contact: Object
  },
  data () {
    return {
      recordType: 'Pledge',
      date: moment().format('YYYY-MM-DD HH:mm'),
      amount: null,
      askResult: null,
      note: '',
      submitError: null
    }
  },
  computed: {
    recordHistory () {
      // combine the records
      let contributions = this.contactContributions || []
      let pledges = this.contactContributionPledges || []
      let asks = this.contactContributionAsks || []
      // combine the records and tag them with the type and a sort key
      const combined = []
      contributions.forEach(record => {
        combined.push({ type: 'contribution', sort: record.date, record })
      })
      pledges.forEach(record => {
        combined.push({ type: 'pledge', sort: record.date, record })
      })
      asks.forEach(record => {
        combined.push({ type: 'ask', sort: record.date, record })
      })
      // sort
      return this.$helpers.sortByKey(combined, 'sort', true)
    },
    isPledge () {
      return this.recordType === 'Pledge'
    },
    isContribution () {
      return this.recordType === 'Contribution'
    },
    isAsk () {
      return this.recordType === 'Ask'
    }
  },
  validations: {
    date: { required },
    amount: {
      required: requiredIf(function () {
        return this.isPledge || this.isContribution
      })
    }
  },
  apollo: {
    contactContributions: {
      query: contactContributionsQuery,
      variables () {
        return {
          id: this.contact.id
        }
      }
    },
    contactContributionPledges: {
      query: contactContributionPledgesQuery,
      variables () {
        return {
          id: this.contact.id
        }
      }
    },
    contactContributionAsks: {
      query: contactContributionAsksQuery,
      variables () {
        return {
          contactId: this.contact.id
        }
      }
    }
  },
  methods: {
    dateChanged () {
      // if the date is cleared, default to today's date
      if (!this.date) {
        this.date = moment().format('YYYY-MM-DD HH:mm')
      }
    },
    clearFormData () {
      this.date = null
      this.amount = null
      this.askResult = null
      this.note = ''
      this.$v.$reset()
      this.dateChanged()
    },
    async handleSubmit () {
      this.submitError = null
      if (this.isPledge) {
        await this.addContributionPledge()
      }
      if (this.isContribution) {
        await this.addContribution()
      }
      if (this.isAsk) {
        await this.addContributionAsk()
      }
    },
    async addContribution () {
      try {
        await this.$apollo.mutate({
          mutation: addContributionMutation,
          variables: {
            contactId: this.contact.id,
            date: this.date,
            amount: this.amount,
            note: this.note.trim()
          },
          // update contact with new contribution balance
          refetchQueries: [{
            query: contactQuery,
            variables: {
              id: this.contact.id
            }
          }]
        })
        // refetch
        await this.$apollo.queries.contactContributions.refetch()
        // clear form
        this.clearFormData()
      } catch (e) {
        this.submitError = e
      }
    },
    async deleteContribution (contributionId) {
      if (!confirm('Are you sure that you want to delete this CONTRIBUTION? This cannot be undone.')) {
        return
      }
      await this.$apollo.mutate({
        mutation: deleteContributionMutation,
        variables: {
          id: contributionId
        },
        // update contact with new contribution balance
        refetchQueries: [{
          query: contactQuery,
          variables: {
            id: this.contact.id
          }
        }]
      })
      await this.$apollo.queries.contactContributions.refetch()
    },
    async addContributionPledge () {
      try {
        await this.$apollo.mutate({
          mutation: addContributionPledgeMutation,
          variables: {
            contactId: this.contact.id,
            input: {
              date: this.date,
              amount: this.amount
            },
            note: this.note.trim()
          },
          // update contact with new pledge balance
          refetchQueries: [{
            query: contactQuery,
            variables: {
              id: this.contact.id
            }
          }]
        })
        // refetch
        await this.$apollo.queries.contactContributionPledges.refetch()
        // clear form
        this.clearFormData()
      } catch (e) {
        this.submitError = e
      }
    },
    async deleteContributionPledge (contributionPledgeId) {
      if (!confirm('Are you sure that you want to delete this PLEDGE? This cannot be undone.')) {
        return
      }
      await this.$apollo.mutate({
        mutation: deleteContributionPledgeMutation,
        variables: {
          id: contributionPledgeId
        },
        // update contact with new pledge balance
        refetchQueries: [{
          query: contactQuery,
          variables: {
            id: this.contact.id
          }
        }]
      })
      await this.$apollo.queries.contactContributionPledges.refetch()
    },
    async dismissContributionPledge (contributionPledgeId, undismiss) {
      await this.$apollo.mutate({
        mutation: dismissContributionPledgeMutation,
        variables: {
          id: contributionPledgeId,
          undismiss: !!undismiss
        },
        // update contact with new pledge balance
        refetchQueries: [{
          query: contactQuery,
          variables: {
            id: this.contact.id
          }
        }]
      })
      await this.$apollo.queries.contactContributionPledges.refetch()
    },
    async addContributionAsk () {
      const addPledge = this.askResult === 'Yes'
      try {
        await this.$apollo.mutate({
          mutation: addContributionAskMutation,
          variables: {
            contactId: this.contact.id,
            input: {
              date: this.date,
              amount: this.amount,
              result: this.askResult || null
            },
            // only add a note if we're not also adding a commitment.
            // if we're adding a commitment, then the note will be added to the commitment, and this
            // will prevent us from creating two duplicate notes.
            note: addPledge ? null : this.note.trim()
          }
        })
        await this.$apollo.queries.contactContributionAsks.refetch()
        // add a pledge?
        if (addPledge) {
          await this.addContributionPledge()
        }
        // clear form
        this.clearFormData()
      } catch (e) {
        this.submitError = e
      }
    },
    async deleteContributionAsk (contributionAskId) {
      try {
        await this.$apollo.mutate({
          mutation: deleteContributionAskMutation,
          variables: {
            id: contributionAskId
          }
        })
        // refetch
        await this.$apollo.queries.contactContributionAsks.refetch()
      } catch (e) {
        this.submitError = e
      }
    }
  }
}
</script>
